import React, { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/20/solid';
import Notification from '../../notification/notification';
import itemApi from '../../../services/itemApi';
import ItemUpsertModal from './item-upsert-modal';
import ModalDelete from '../../modal-delete/modal-delete';
import ItemCard from './item-card';
import { TWButton } from '../../ui';
import { ItemListSkeleton } from '../../skeleton/skeleton';
import { capitalizeWords } from '../../../utils/baseUtils';
import { emptyItemForm, itemFormFromItem } from './item-form-model';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

export default function ItemList({
  selected,
  setSelected,
  onItemCountChange,
  hideHeader = false,
}) {
  const [deletedItem, setDeletedItem] = useState({ name: '', id: '' });
  const [isSingleItem, setIsSingleItem] = useState(true);
  const [items, setItems] = useState([{ name: '', price: '', calories: '' }]);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: false,
    price: false,
  });
  const [itemForm, setItemForm] = useState(
    emptyItemForm(selected.product?.id, 0),
  );
  const [itemDialogs, setItemDialogs] = useState({
    itemUpsert: false,
    itemDelete: false,
  });
  const [itemList, setItemList] = useState([]);
  const [loading, setLoading] = useState({
    itemLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });

  // Drop the Calories column entirely unless at least one item has a
  // customer-visible calorie — a value that is null, or entered but hidden
  // (showcalories=false), does not earn the column. Products that don't use
  // calories get a clean Name / Price / Actions table.
  const showCaloriesColumn = itemList.some(
    (it) => it.calories != null && it.showcalories !== false,
  );
  const headers = ['Name', 'Price', ...(showCaloriesColumn ? ['Calories'] : [])];
  const getItemList = async (method) => {
    try {
      setLoading((prev) => ({ ...prev, itemLoading: true }));
      const list = await itemApi.list(selected.product.id);
      if (list.data.data.length > 0) {
        setItemList(list.data.data);
      } else {
        setItemList([]);
        setSelected((prev) => ({
          ...prev,
          itemid: '',
        }));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, itemLoading: false }));
    }
  };

  const onClickAddItem = () => {
    setItemForm(
      emptyItemForm(
        selected.product.id,
        itemList.length > 0
          ? itemList[itemList.length - 1].displayorder + 1
          : 1,
        itemList.length > 0 ? '' : selected.product.name,
      ),
    );

    setItemDialogs((prev) => ({ ...prev, itemUpsert: true }));
  };

  const onFormChange = (val, key) => {
    setItemForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickDeleteItem = (item) => {
    if (itemList.length === 1 && selected.product.isupsell) {
      setNotf((prev) => ({
        ...prev,
        message: 'You must remove the upsell first from product',
        display: true,
      }));
      return;
    }

    setDeletedItem(item);
    setItemDialogs((prev) => ({ ...prev, itemDelete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await itemApi.deleteById(deletedItem.id);
      getItemList();
    } catch (error) {
    } finally {
      setItemDialogs((prev) => ({
        ...prev,
        itemDelete: false,
        itemUpsert: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const onClickEditItem = (val) => {
    setItemForm(itemFormFromItem(val, selected.product.id));

    setItemDialogs((prev) => ({ ...prev, itemUpsert: true }));
  };

  const onClickDuplicateItem = async (item) => {
    try {
      const res = await itemApi.duplicate(item.id);
      const newName = res?.data?.data?.name || res?.data?.name || 'item';
      setNotf({
        display: true,
        type: 'success',
        message: `Duplicated as "${newName}"`,
      });
      await getItemList();
    } catch (e) {
      setNotf({
        display: true,
        type: 'error',
        message:
          e?.response?.data?.Message ||
          e?.response?.data ||
          'Duplicate failed',
      });
    }
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({
      ...prev,
      name: false,
      price: false,
      displayorder: false,
    }));
    setItemDialogs((prev) => ({ ...prev, itemUpsert: false }));
    setItems([{ name: '', price: '', calories: '' }]);
    setIsSingleItem(true);
  };

  const onFormSubmit = async () => {
    if (!itemForm.name || itemForm.price === '') {
      setErrors((prev) => ({
        ...prev,
        name: !itemForm.name ? true : false,
        price: itemForm.price === '' ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, name: false }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...itemForm };
      // Capitalize item name before saving
      if (body.name) {
        body.name = capitalizeWords(body.name.trim());
      }
      const res = await itemApi.upsert(body);
      if (res.data.data) {
        setItemDialogs((prev) => ({ ...prev, itemUpsert: false }));
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getItemList({ method: itemForm.id ? 'update' : 'create' });
    }
  };

  const onFormSubmitMultiple = async () => {
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      for (const [, item] of items.entries()) {
        if (!item.name || item.price === '') {
          setErrors((prev) => ({
            ...prev,
            name: !item.name ? true : false,
            price: item.price === '' ? true : false,
          }));
          return;
        } else {
          setErrors((prev) => ({ ...prev, name: false }));
        }
      }

      for (const [index, item] of items.entries()) {
        const body = {
          name: item.name ? capitalizeWords(item.name.trim()) : item.name,
          productid: selected.product.id,
          price: item.price,
          displayorder: index + 1,
          calories: item.calories,
        };
        await itemApi.upsert(body);
      }
      setIsSingleItem(true);
      setItemDialogs((prev) => ({ ...prev, itemUpsert: false }));
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      getItemList();
    } catch (error) {}
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = itemList.findIndex((item) => item.id === active.id);
    const newIndex = itemList.findIndex((item) => item.id === over.id);
    const reordered = arrayMove(itemList, oldIndex, newIndex);

    setItemList(reordered);

    try {
      await itemApi.reorder({
        items: reordered.map((item, index) => ({
          id: item.id,
          displayorder: index,
        })),
      });
    } catch (error) {
      console.error('Error reordering items:', error);
      await getItemList();
    }
  };

  useEffect(() => {
    getItemList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.product.id]);

  useEffect(() => {
    if (typeof onItemCountChange === 'function') {
      onItemCountChange(itemList.length);
    }
  }, [itemList.length, onItemCountChange]);
  return (
    <>
      {loading.itemLoading ? (
        <ItemListSkeleton />
      ) : (
        <div className="px-3 pb-3 pt-2">
          {!hideHeader && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-gradient-to-r from-white to-slate-50 px-4 py-3 dark:border-slate-700 dark:from-slate-800 dark:to-slate-800/80">
              <div>
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  Main Option
                </p>
                <h1 className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Product Items
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Manage item names and prices shown to customers.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                  {itemList.length} Item{itemList.length > 1 ? 's' : ''}
                </span>
                <TWButton
                  variant="primary"
                  size="sm"
                  icon={PlusIcon}
                  label="Item"
                  onClick={() => onClickAddItem()}
                />
              </div>
            </div>
          )}
          {hideHeader && (
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {itemList.length} item{itemList.length > 1 ? 's' : ''}
              </span>
              <TWButton
                variant="primary"
                size="sm"
                icon={PlusIcon}
                label="Add Item"
                onClick={() => onClickAddItem()}
              />
            </div>
          )}

          <div className="overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <div className="-mx-4 overflow-x-auto">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 px-4">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-500">
                  <thead>
                    <tr>
                      {headers.map((head, headIdx) => (
                        <th
                          key={headIdx}
                          scope="col"
                          className={`py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-zinc-900 dark:text-slate-200 ${
                            headIdx === 0 ? 'sm:pl-6' : ''
                          } `}
                        >
                          {head}
                        </th>
                      ))}
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-4 text-right text-sm font-semibold text-zinc-900 dark:text-slate-200"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToVerticalAxis]}
                  >
                    <SortableContext
                      items={itemList.map((it) => it.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <tbody className="divide-y divide-gray-200 dark:divide-slate-500">
                        {itemList.map((item, i) => (
                          <ItemCard
                            key={item.id}
                            index={i}
                            id={item.id}
                            onClickEditItem={onClickEditItem}
                            onClickDuplicateItem={onClickDuplicateItem}
                            productItem={item}
                            showCalories={showCaloriesColumn}
                          />
                        ))}
                      </tbody>
                    </SortableContext>
                  </DndContext>
                </table>
              </div>
            </div>
            {itemList.length === 0 && (
              <div className="border-t border-slate-200 px-6 py-10 text-center dark:border-slate-700">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  No item yet
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Create the first item to set base price and name.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <ItemUpsertModal
        show={itemDialogs.itemUpsert}
        loading={loading.upsertLoading}
        itemForm={itemForm}
        errors={errors}
        onClickDeleteItem={onClickDeleteItem}
        onCloseUpsert={onCloseUpsert}
        onFormChange={onFormChange}
        onFormSubmit={onFormSubmit}
        setIsSingleItem={setIsSingleItem}
        isSingleItem={isSingleItem}
        items={items}
        itemList={itemList}
        setItems={setItems}
        onFormSubmitMultiple={onFormSubmitMultiple}
      />

      <ModalDelete
        title="Item"
        text={`Are you sure you want to delete "${deletedItem.name}"? All associated data will be permanently removed.`}
        loading={loading.deleteLoading}
        open={itemDialogs.itemDelete}
        onCloseRequest={() =>
          setItemDialogs((prev) => ({ ...prev, itemDelete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedItem}
      />
      <Notification
        classAttributes="pt-20"
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </>
  );
}
